import React, { useState } from 'react';
import { ArrowRight, Shield, Lock, CheckCircle, Users, Zap, Eye } from 'lucide-react';
import Navbar from '../components/Navbar.tsx';
import Footer from '../components/Footer.tsx';
import InfoTooltip from '../components/InfoTooltip.tsx';
import UpgradeButton from '../components/UpgradeButton.tsx';
import UploadWizard, { WizardContainer } from '../components/UploadWizard/index.tsx';
import { triggerDemoScan } from '../services/demoService.ts';
import PostHogService from '../services/postHogService.ts';
import { subscribeToNewsletter } from '../services/apiService.ts';

// Mock session context - will be replaced with actual auth later
const useMockSession = () => ({
  user: null,
  loading: false
});

const devTools = [
  { name: 'Bolt.new', logo: '/logos/bolt.svg' },
  { name: 'Lovable', logo: '/logos/lovable.svg' },
  { name: 'Replit', logo: '/logos/replit.svg' },
  { name: 'Vitara', logo: '/logos/vitara.svg' },
  { name: 'Softgen', logo: '/logos/softgen.svg' },
  { name: 'Custom stacks', logo: '/logos/custom.svg' },
];

function ToolLogo({ name, logo }: { name: string; logo: string }) {
  return (
    <span className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow hover:scale-105 transition border border-gray-200">
      <img src={logo} alt={`${name} logo`} className="w-6 h-6" />
      <span className="font-medium text-gray-800">{name}</span>
    </span>
  );
}

export default function HomeScreen() {
  const [email, setEmail] = useState('');
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError('');
    setIsSubmitting(true);
    
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setEmailError('Please enter a valid email address.');
      setIsSubmitting(false);
      return;
    }
    
    try {
      // Save email to database via Edge Function
      console.log('[HomeScreen] Subscribing email to newsletter:', email);
      const result = await subscribeToNewsletter(email);
      
      if (result.success) {
        // Track email signup event with PostHog
        PostHogService.trackSignup('email_newsletter', {
          source: 'homepage_newsletter',
          email_domain: email.split('@')[1]
        });
        
        setEmailSubmitted(true);
        setEmail(''); // Clear the form
        setTimeout(() => setEmailSubmitted(false), 5000);
        console.log('[HomeScreen] Newsletter subscription successful');
      } else {
        throw new Error(result.message || 'Subscription failed');
      }
    } catch (error) {
      console.error('[HomeScreen] Newsletter subscription error:', error);
      setEmailError('Failed to subscribe. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 font-sans flex flex-col">
      <Navbar />
      
      <main className="flex-1">
        {/* Enhanced Hero Section */}
        <section id="upload-bucket" className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
          {/* Background Effects */}
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 to-purple-600/10"></div>
          
          <div className="relative container mx-auto px-6 pt-20 pb-32">
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-16 items-center">
                
                {/* Left Column - Hero Content */}
                <div className="text-center lg:text-left space-y-8">
                  {/* Trust Badge */}
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 border border-green-200 rounded-full text-green-700 text-sm font-medium">
                    <CheckCircle className="w-4 h-4" />
                    <span>Made for devs who ship fast</span>
                  </div>

                  {/* Main Headline */}
                  <div className="space-y-4">
                    <h1 className="text-5xl lg:text-7xl font-extrabold text-gray-900 leading-tight min-h-[6.5rem] lg:min-h-[9rem] overflow-visible" style={{lineHeight: '1.1'}}>
                      Vibe Coding—
                      <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                        But Make It Secure.
                      </span>
                    </h1>
                    {/* Max persona badge */}
                    <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 border border-blue-200 rounded-full text-blue-700 text-sm font-semibold mb-2">
                      🎉 Built by Bolt.new alumni
                    </div>
                    <p className="text-xl lg:text-2xl text-gray-600 max-w-2xl leading-relaxed">
                      Scan, fix, and ship with confidence—
                      <span className="text-gray-900 font-semibold">AI-powered security for your flow.</span>
                    </p>
                  </div>
                  {/* Demo Scan CTA */}
                  <div className="my-6 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                    <button
                      onClick={() => triggerDemoScan()}
                      className="px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-xl font-bold text-lg shadow-lg transition-all duration-200 transform hover:scale-105 hover:shadow-2xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2"
                    >
                      Start Demo Scan → <span className="ml-2 text-base font-normal">Takes &lt; 30 seconds — no signup needed</span>
                    </button>
                  </div>
                  {/* Max persona supporting text */}
                  <div className="text-blue-700 font-medium text-base mb-4">
                    Use it on your hackathon submission — get instant fix tips.
                  </div>
                  {/* Social Proof Stats */}
                  <div className="flex flex-wrap gap-8 justify-center lg:justify-start items-center">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-gray-900">50K+</div>
                      <div className="text-sm text-gray-500">Scans completed</div>
                    </div>
                    <div className="text-center flex items-center gap-2">
                      <div className="text-3xl font-bold text-gray-900">19</div>
                      <div className="text-sm text-gray-500">Security Scanners</div>
                      <a href="#scanner-list" className="ml-2 text-blue-600 underline hover:text-blue-800 text-sm font-medium transition-colors">See full list →</a>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-gray-900">&lt; 30s</div>
                      <div className="text-sm text-gray-500">Average scan time</div>
                    </div>
                  </div>
                  {/* Blog link for Leila persona */}
                  <div className="mt-6">
                    <a href="/blog/how-ai-explains-code-risks" className="inline-flex items-center gap-2 px-4 py-2 bg-purple-50 border border-purple-200 rounded-lg text-purple-700 font-medium hover:bg-purple-100 transition">
                      🧠 How our AI explains real code risks →
                    </a>
                  </div>

                  {/* Built For Section */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 justify-center lg:justify-start">
                      <Zap className="w-5 h-5 text-blue-600" />
                      <span className="text-blue-600 font-semibold text-lg">Built for modern dev tools</span>
                    </div>
                    <div className="flex flex-wrap gap-4 justify-center lg:justify-start">
                      {/* Only show Bolt.new for hackathon compliance; others commented out */}
                      <ToolLogo name="Bolt.new" logo="/logos/bolt.svg" />
                      {false && devTools.filter(tool => tool.name !== 'Bolt.new').map((tool) => (
                        <ToolLogo key={tool.name} name={tool.name} logo={tool.logo} />
                      ))}
                    </div>
                  </div>
                </div>

                {/* Right Column - Enhanced Upload Widget */}
                <div className="relative">
                  {/* Glow Effect */}
                  <div className="absolute -inset-4 bg-gradient-to-r from-blue-200/50 to-purple-200/50 rounded-3xl blur-xl opacity-75"></div>
                  
                  {/* Upload Card */}
                  <div className="relative bg-white backdrop-blur-xl rounded-2xl shadow-xl border border-gray-200 p-8">
                    <div className="text-center mb-6">
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">Start Your Security Scan</h3>
                      <p className="text-gray-600">Upload your code and get instant security insights</p>
                    </div>
                    
                    <UploadWizard variant="hero" />
                    
                    {/* Quick Benefits */}
                    <div className="mt-6 space-y-3">
                      <div className="flex items-center gap-3 text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <span>Instant AI-powered security analysis</span>
                      </div>
                      <div className="flex items-center gap-3 text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <span>Find API keys, credentials & privacy leaks</span>
                      </div>
                      <div className="flex items-center gap-3 text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <span>Get fix suggestions, not just problems</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Enhanced Problem Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                What Could Go Wrong?
                <span className="block text-red-600">(More Than You Think)</span>
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                Even experienced builders miss costly security flaws. One leaked API key or privacy violation can instantly derail your project.
              </p>
            </div>

            {/* Threat Examples */}
            <div className="grid md:grid-cols-3 gap-8 mb-16">
              <div className="bg-gradient-to-br from-red-50 to-red-100 border border-red-200 rounded-xl p-6">
                <div className="text-3xl mb-4">💸</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Exposed API Keys</h3>
                <p className="text-gray-600 text-sm mb-4">One leaked OpenAI key = $1000+ bill overnight</p>
                <div className="text-xs text-red-700 bg-red-100 px-3 py-2 rounded-lg border border-red-200">
                  Real cost: $2,847 in 6 hours
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200 rounded-xl p-6">
                <div className="text-3xl mb-4">⚖️</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">GDPR Violations</h3>
                <p className="text-gray-600 text-sm mb-4">Leaked customer data destroys trust and risks massive fines</p>
                <div className="text-xs text-orange-700 bg-orange-100 px-3 py-2 rounded-lg border border-orange-200">
                  Fines up to €20M or 4% revenue
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-xl p-6">
                <div className="text-3xl mb-4">🔓</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Data Breaches</h3>
                <p className="text-gray-600 text-sm mb-4">Unsecured databases expose sensitive user information</p>
                <div className="text-xs text-purple-700 bg-purple-100 px-3 py-2 rounded-lg border border-purple-200">
                  Average cost: $4.45M per breach
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Enhanced Features Grid */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center mb-16">
              <h2 id="scanner-list" className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                🚀 Security Scans You Actually Understand
              </h2>
              <p className="text-xl text-gray-600">
                Clear, jargon-free results with AI-powered fix suggestions
              </p>
            </div>

            <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Free Features */}
              <div className="group bg-white rounded-xl p-6 border border-gray-200 hover:border-blue-400 transition-all duration-300 hover:transform hover:scale-[1.02] shadow-sm hover:shadow-lg">
                <div className="flex items-center justify-between mb-4">
                  <Shield className="h-8 w-8 text-blue-500" />
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-semibold border border-green-200">FREE</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">API Key Detection</h3>
                <p className="text-gray-600 text-sm mb-4">Find leaked OpenAI, GitHub, and other API keys in your code</p>
                <div className="space-y-2 text-xs text-gray-500">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-3 h-3 text-green-500" />
                    <span>50+ API patterns detected</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-3 h-3 text-green-500" />
                    <span>Instant fix suggestions</span>
                  </div>
                </div>
              </div>

              <div className="group bg-white rounded-xl p-6 border border-gray-200 hover:border-yellow-400 transition-all duration-300 hover:transform hover:scale-[1.02] shadow-sm hover:shadow-lg">
                <div className="flex items-center justify-between mb-4">
                  <Lock className="h-8 w-8 text-yellow-500" />
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-semibold border border-green-200">FREE</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Credential Scanner</h3>
                <p className="text-gray-600 text-sm mb-4">Detect hardcoded passwords and tokens in source files</p>
                <div className="space-y-2 text-xs text-gray-500">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-3 h-3 text-green-500" />
                    <span>Pattern-based detection</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-3 h-3 text-green-500" />
                    <span>Context-aware alerts</span>
                  </div>
                </div>
              </div>

              <div className="group bg-white rounded-xl p-6 border border-gray-200 hover:border-purple-400 transition-all duration-300 hover:transform hover:scale-[1.02] shadow-sm hover:shadow-lg">
                <div className="flex items-center justify-between mb-4">
                  <Eye className="h-8 w-8 text-purple-500" />
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-semibold border border-green-200">FREE</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Privacy Scanner</h3>
                <p className="text-gray-600 text-sm mb-4">Find PII in logs and analytics code</p>
                <div className="space-y-2 text-xs text-gray-500">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-3 h-3 text-green-500" />
                    <span>GDPR compliance checks</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-3 h-3 text-green-500" />
                    <span>Email & phone detection</span>
                  </div>
                </div>
              </div>

              {/* Premium Features */}
              <div className="group bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-6 border border-purple-200 hover:border-purple-400 transition-all duration-300 hover:transform hover:scale-[1.02] shadow-sm hover:shadow-lg relative overflow-hidden">
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-purple-100 to-blue-100 rounded-bl-full"></div>
                <div className="flex items-center justify-between mb-4">
                  <div className="text-2xl">🌐</div>
                  <span className="text-xs bg-gradient-to-r from-purple-100 to-blue-100 text-purple-700 px-2 py-1 rounded-full font-semibold border border-purple-200">PRO</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">API Security Scan</h3>
                <p className="text-gray-600 text-sm mb-4">Find unprotected endpoints and rate limiting issues</p>
                <div className="space-y-2 text-xs text-gray-500">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-3 h-3 text-purple-500" />
                    <span>Auth bypass detection</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-3 h-3 text-purple-500" />
                    <span>Rate limit analysis</span>
                  </div>
                </div>
              </div>

              <div className="group bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-6 border border-purple-200 hover:border-purple-400 transition-all duration-300 hover:transform hover:scale-[1.02] shadow-sm hover:shadow-lg relative overflow-hidden">
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-purple-100 to-blue-100 rounded-bl-full"></div>
                <div className="flex items-center justify-between mb-4">
                  <div className="text-2xl">🧨</div>
                  <span className="text-xs bg-gradient-to-r from-purple-100 to-blue-100 text-purple-700 px-2 py-1 rounded-full font-semibold border border-purple-200">PRO</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Config Security</h3>
                <p className="text-gray-600 text-sm mb-4">Detect insecure defaults in .env and database configs</p>
                <div className="space-y-2 text-xs text-gray-500">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-3 h-3 text-purple-500" />
                    <span>Supabase RLS checks</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-3 h-3 text-purple-500" />
                    <span>Environment validation</span>
                  </div>
                </div>
              </div>

              <div className="group bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-6 border border-purple-200 hover:border-purple-400 transition-all duration-300 hover:transform hover:scale-[1.02] shadow-sm hover:shadow-lg relative overflow-hidden">
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-purple-100 to-blue-100 rounded-bl-full"></div>
                <div className="flex items-center justify-between mb-4">
                  <div className="text-2xl">🔎</div>
                  <span className="text-xs bg-gradient-to-r from-purple-100 to-blue-100 text-purple-700 px-2 py-1 rounded-full font-semibold border border-purple-200">PRO</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">AI Code Analysis</h3>
                <p className="text-gray-600 text-sm mb-4">Advanced static analysis for XSS, injection, and logic flaws</p>
                <div className="space-y-2 text-xs text-gray-500">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-3 h-3 text-purple-500" />
                    <span>ML-powered detection</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-3 h-3 text-purple-500" />
                    <span>Zero false positives</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Enhanced CTA Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center">
              <div className="mb-6">
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-purple-50 border border-purple-200 rounded-full text-purple-700 text-sm font-medium">
                  <Users className="w-4 h-4" />
                  <span>Join 1,000+ developers shipping securely</span>
                </span>
              </div>
              
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                🚀 Ship with Confidence
              </h2>
              <p className="text-xl text-gray-600 mb-10 leading-relaxed">
                Get AI-powered security fixes, not just red flags. 
                <span className="text-gray-900 font-semibold"> Upgrade to Pro for complete protection.</span>
              </p>

              <div className="grid md:grid-cols-2 gap-8 mb-10">
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">✨ AI Fix Power</h3>
                  <ul className="space-y-3 text-gray-600 text-left">
                    <li className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span>Instant AI-powered fix suggestions</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span>Secure code replacements for each issue</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span>Advanced scans: prompt injection, unsafe RLS</span>
                    </li>
                  </ul>
                </div>
                
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">🚀 Pro Perks</h3>
                  <ul className="space-y-3 text-gray-600 text-left">
                    <li className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-purple-500 flex-shrink-0" />
                      <span>Runtime & API risk detection</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-purple-500 flex-shrink-0" />
                      <span>Priority scan speed</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-purple-500 flex-shrink-0" />
                      <span>Future integrations & features</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <UpgradeButton
                  label="🚀 Get Pro Access"
                  className="text-lg font-semibold px-8 py-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Newsletter Section */}
        <section className="py-16 bg-blue-600">
          <div className="container mx-auto px-6">
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="text-3xl font-bold text-white mb-4">
                Stay Updated on Security
              </h2>
              <p className="text-blue-100 mb-8">
                Get weekly tips on securing your web apps, plus early access to new features.
              </p>
              
              <form onSubmit={handleEmailSubmit} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
                <input
                  type="email"
                  value={email}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-3 rounded-lg border-0 focus:ring-2 focus:ring-blue-300 focus:outline-none text-gray-900"
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-6 py-3 bg-white text-blue-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors disabled:opacity-50"
                >
                  {isSubmitting ? 'Subscribing...' : 'Subscribe'}
                </button>
              </form>
              
              {emailSubmitted && (
                <div className="mt-4 text-green-100 font-medium">
                  ✅ Thanks! You're subscribed to security updates.
                </div>
              )}
              
              {emailError && (
                <div className="mt-4 text-red-100 font-medium">
                  {emailError}
                </div>
              )}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
} 